import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { FiUser, FiPhone, FiMail, FiMapPin, FiCalendar, FiCreditCard, FiEye, FiEyeOff, FiLock, FiRotateCcw } from 'react-icons/fi';
import { toast } from '../utils/toast';
import TopBar from '../components/TopBar';
import Loading from '../components/Loading';
import PasswordService from '../services/passwordService';

const PatientCardPage = () => {
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [hospitalPhone, setHospitalPhone] = useState('');
  const [hospitalName, setHospitalName] = useState('');
  const [hospitalEmail, setHospitalEmail] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [isFlipped, setIsFlipped] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('userInfo'));
    const hospital_name = JSON.parse(localStorage.getItem('hospital_name'));
    const hospital_phone = JSON.parse(localStorage.getItem('hospital_phone'));
    const hospital_email = JSON.parse(localStorage.getItem('hospital_email'));

    setHospitalName(hospital_name);
    setHospitalEmail(hospital_email);
    setHospitalPhone(hospital_phone);
    setUserInfo(userData);
    
    if (userData?.qrcode) {
      generateQRCode(userData.qrcode);
    }
  }, []);

  const generateQRCode = async (qrData) => {
    try {
      const url = await QRCode.toDataURL(qrData, {
        width: 100,
        margin: 1,
        color: {
          dark: 'var(--color-primary, #087F4C)',
          light: '#FFFFFF'
        }
      });
      setQrCodeUrl(url);
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };

  const handleCardClick = () => {
    setIsFlipped(!isFlipped);
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    
    setIsUpdatingPassword(true);
    
    try {
      const result = await PasswordService.changePassword(
        passwordData.currentPassword,
        passwordData.newPassword,
        passwordData.confirmPassword
      );

      if (result.success) {
        toast.success(result.message || 'Password updated successfully');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setShowPasswordForm(false);
        
        toast.info(result?.response?.data?.message || 'A confirmation email has been sent to your email address.');
        }
    } catch (error) {
       toast.error(error?.response?.data?.message || error.message);
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  if (!userInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary100/10 to-secondary100">
        <TopBar title="Patient Card" url={-1} />
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary100 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading patient information...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary100/10 to-secondary100">
      <TopBar title="Patient Card" url={-1} />
      
      <div className="px-4 py-6 space-y-6">
        {/* Card Container with Flip Animation */}
        <div className="relative w-full h-64 perspective-1000">
          <div 
            className={`relative w-full h-full transition-transform duration-700 transform-style-preserve-3d cursor-pointer ${
              isFlipped ? 'rotate-y-180' : ''
            }`}
            onClick={handleCardClick}
          >
            {/* Front of Card */}
            <div className="absolute inset-0 w-full h-full backface-hidden">
              <div className="bg-primary100 rounded-2xl p-6 text-white h-full relative overflow-hidden shadow-xl">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-4 left-4 w-8 h-8 bg-white rounded-full animate-pulse"></div>
                  <div className="absolute top-12 right-8 w-4 h-4 bg-white rounded-full animate-pulse delay-100"></div>
                  <div className="absolute bottom-8 left-8 w-6 h-6 bg-white rounded-full animate-pulse delay-200"></div>
                  <div className="absolute bottom-4 right-4 w-3 h-3 bg-white rounded-full animate-pulse delay-300"></div>
                </div>

                {/* Card Header */}
                <div className="relative z-10 flex justify-between items-start mb-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                      <span className="text-primary100 font-bold text-lg">M</span>
                    </div>
                    <div>
                      <span className="text-sm font-bold">MISAS</span>
                      <p className="text-xs font-bold opacity-80">{hospitalName}</p>
                    </div>
                  </div>
                  <span className="text-xs font-sx bg-white/20 px-3 py-1 rounded-full backdrop-blur-sm">
                    Virtual
                  </span>
                </div>


                {/* Patient Info and QR Code */}
                <div className="relative z-10 flex justify-between items-end">
                  <div className="flex-1">
                    <div className="mb-2">
                      <p className="text-xs opacity-80">Patient Name</p>
                      <p className="font-bold text-sm">
                        {userInfo.title} {userInfo.firstname} {userInfo.lastname}
                      </p>
                    </div>
                    <div className="mb-2">
                      <p className="text-xs opacity-80">Patient ID</p>
                      <p className="font-bold text-lg">{userInfo.system_id}</p>
                    </div>
                    <div>
                      <p className="text-xs opacity-80">Gender</p>
                      <p className="font-medium text-sm">{userInfo.gender}</p>
                    </div>
                  </div>
                  
                  {/* QR Code */}
                  <div className="bg-white p-2 rounded-lg shadow-lg">
                    {qrCodeUrl ? (
                      <img src={qrCodeUrl} alt="Patient QR Code" className="w-16 h-16" />
                    ) : (
                      <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
                        <span className="text-gray-500 text-xs">Loading...</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Back of Card */}
            <div className="absolute inset-0 w-full h-full backface-hidden rotate-y-180">
              <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl p-6 h-full relative overflow-hidden shadow-xl">
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-primary100 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-sm">M</span>
                    </div>
                    <span className="text-sm font-bold text-gray-700">{hospitalName }</span>
                  </div>
                  <span className="text-lg font-bold text-primary100">{userInfo.system_id}</span>
                </div>

                {/* Terms and Conditions */}
                <div className="space-y-2 text-xs text-gray-600">
                  <p className="font-medium">
                    This card is issued by {hospitalName}. If found please return to {hospitalName}.
                  </p>
                  <p>
                    This card may be used by named card holder only and in accordance with {hospitalName} Card terms and conditions of use.
                  </p>
                  <div className="border-t border-gray-300 pt-3 mt-2">
                    <p className="font-medium text-gray-700">Contact Information:</p>
                    <p>Email: {hospitalEmail}</p>
                    <p>Phone: {hospitalPhone}</p>
                  </div>
                </div>

                {/* Footer */}
                <div className="absolute bottom-4 right-4">
                  <div className="text-right">
                    <p className="text-xs text-primary100 font-bold">MISAS</p>
                    <p className="text-xs text-gray-500">www.misas.com.ng</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Flip Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsFlipped(!isFlipped);
            }}
            className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-white shadow-lg rounded-full p-3 hover:shadow-xl transition-all duration-300 border border-gray-200"
          >
            <FiRotateCcw className="text-primary100 text-lg" />
          </button>
        </div>

        {/* Tap to Flip Hint */}
        <div className="text-center">
          <p className="text-gray-500 text-sm">Tap the card or flip button to see the back</p>
        </div>

        {/* Patient Details */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-primary100/20">
          <h4 className="font-bold text-gray-800 text-lg mb-4 flex items-center">
            <FiUser className="mr-2 text-primary100" />
            Patient Information
          </h4>
          
          <div className="grid grid-cols-1 gap-4">
            <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-primary100/5 to-secondary100 rounded-xl border border-primary100/10">
              <FiUser className="text-primary100 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 font-medium">Full Name</p>
                <p className="font-semibold text-gray-800 truncate">
                  {userInfo.title} {userInfo.firstname} {userInfo.middlename} {userInfo.lastname}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-primary100/5 to-secondary100 rounded-xl border border-primary100/10">
              <FiCreditCard className="text-primary100 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 font-medium">Patient ID</p>
                <p className="font-semibold text-gray-800 truncate">{userInfo.system_id}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-primary100/5 to-secondary100 rounded-xl border border-primary100/10">
                <FiPhone className="text-primary100 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 font-medium">Mobile</p>
                  <p className="font-semibold text-gray-800 text-sm truncate">{userInfo.mobile}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-primary100/5 to-secondary100 rounded-xl border border-primary100/10">
                <FiMail className="text-primary100 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 font-medium">Email</p>
                  <p className="font-semibold text-gray-800 text-sm truncate" title={userInfo.email}>
                    {userInfo.email}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-primary100/5 to-secondary100 rounded-xl border border-primary100/10">
              <FiMapPin className="text-primary100 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 font-medium">Location</p>
                <p className="font-semibold text-gray-800 truncate">{userInfo.state}, {userInfo.country}</p>
              </div>
            </div>

            {userInfo.dob && userInfo.dob !== '0000-00-00' && (
              <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-primary100/5 to-secondary100 rounded-xl border border-primary100/10">
                <FiCalendar className="text-primary100 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 font-medium">Date of Birth</p>
                  <p className="font-semibold text-gray-800 truncate">{userInfo.dob}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Password Update Section */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-primary100/20">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-bold text-gray-800 text-lg flex items-center">
              <FiLock className="mr-2 text-primary100" />
              Security Settings
            </h4>
            <button
              onClick={() => setShowPasswordForm(!showPasswordForm)}
              className="bg-primary100 text-white px-4 py-2 rounded-xl font-medium hover:opacity-90 transition-all duration-300 text-sm shadow-lg"
            >
              {showPasswordForm ? 'Cancel' : 'Change Password'}
            </button>
          </div>

          {showPasswordForm && (
            <form onSubmit={handlePasswordUpdate} className="space-y-4">
              {/* Current Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary100 focus:border-transparent pr-12"
                    placeholder="Enter current password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('current')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPasswords.current ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary100 focus:border-transparent pr-12"
                    placeholder="Enter new password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPasswords.new ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary100 focus:border-transparent pr-12"
                    placeholder="Confirm new password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPasswords.confirm ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                {isUpdatingPassword ? (
                  <Loading fullScreen={false} message="Updating Password..." variant="medical" size="medium" />
                ) : (
                  <button
                    type="submit"
                    disabled={isUpdatingPassword}
                    className="w-full py-3 bg-primary100 hover:bg-primary100/90 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg text-white font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                  >
                    Update Password
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientCardPage;